import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';

const ReportsView = ({ clients, onUpdateClient, onOpenClient }) => {
    const [stats, setStats] = useState({});
    const [loadingStats, setLoadingStats] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'inactive'

    useEffect(() => {
        const fetchStats = async () => {
            setLoadingStats(true);
            const newStats = {};

            // Parallel fetch for all clients
            await Promise.all(clients.map(async (client) => {
                const meetings = await DataManager.getClientMeetings(client.id, true);
                const validMeetings = meetings.filter(m => !m.deletedAt);

                const parseDate = (d) => {
                    if (!d) return 0;
                    if (typeof d === 'string' && d.includes('/')) {
                        const p = d.split('/');
                        return new Date(p[2], p[1] - 1, p[0]).getTime();
                    }
                    return new Date(d).getTime();
                };

                const sorted = validMeetings.sort((a, b) => parseDate(a.date) - parseDate(b.date));
                const lastMeeting = sorted.length > 0 ? sorted[sorted.length - 1].date : null;

                // Recupero Snapshot Anamnesis per ottenere l'età, o calcolo da birthDate se presente
                let eta = '-';
                if (client.birthDate) {
                    const dob = new Date(client.birthDate);
                    const diffMs = Date.now() - dob.getTime();
                    const ageDt = new Date(diffMs); 
                    const ageYears = Math.abs(ageDt.getUTCFullYear() - 1970);
                    if (!isNaN(ageYears)) {
                        eta = `${ageYears}`;
                    }
                }

                if (eta === '-') {
                    try {
                        const snapshot = await DataManager.getAnamnesisSnapshot(client.id);
                        if (snapshot && snapshot.eta && snapshot.eta.current) {
                            eta = snapshot.eta.current.value || '-';
                        }
                    } catch(e) { /* ignore silently */ }
                }

                newStats[client.id] = {
                    count: validMeetings.length,
                    lastDate: lastMeeting,
                    eta: eta
                };
            }));

            setStats(newStats);
            setLoadingStats(false);
        };

        if (clients.length > 0) {
            fetchStats();
        } else {
            setLoadingStats(false);
        }
    }, [clients]);

    const handleStatusChange = async (client, newStatus) => {
        const isActive = newStatus === 'active';
        // Optimistic update
        onUpdateClient({ ...client, isActive });
        await DataManager.updateClientMode(client.id, client.mode, isActive);
    };

    const handlePaymentToggle = async (client) => {
        const statuses = ['Regolare', 'In Sospeso', 'Da Verificare'];
        const currentIdx = statuses.indexOf(client.paymentStatus || 'Regolare');
        const nextStatus = statuses[(currentIdx + 1) % statuses.length];

        onUpdateClient({ ...client, paymentStatus: nextStatus });
        await DataManager.updateClient(client.id, { paymentStatus: nextStatus });
    };

    // Filtering logic
    const filteredClients = clients.filter(client => {
        // 1. Status Filter
        const isActive = client.isActive !== false;
        if (statusFilter === 'active' && !isActive) return false;
        if (statusFilter === 'inactive' && isActive) return false;

        // 2. Search Filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (
                client.name.toLowerCase().includes(term) ||
                client.email.toLowerCase().includes(term)
            );
        }
        return true;
    });

    if (loadingStats) {
        return <div className="p-8 text-center text-gray-500">Caricamento statistiche in corso...</div>;
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center gap-4 flex-wrap">
                {/* Status Tabs */}
                <div className="flex bg-gray-100 p-1 rounded-xl">
                    <button
                        onClick={() => setStatusFilter('active')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition ${statusFilter === 'active' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        In Corso
                    </button>
                    <button
                        onClick={() => setStatusFilter('inactive')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition ${statusFilter === 'inactive' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Conclusi / Inattivi
                    </button>
                </div>

                {/* Search Bar */}
                <div className="relative flex-1 max-w-md">
                    <input
                        type="text"
                        placeholder="🔍 Cerca cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-3 border border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    />
                </div>
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                <th className="p-4">Cliente</th>
                                <th className="p-4 text-center">Età</th>
                                <th className="p-4 text-center">Incontri</th>
                                <th className="p-4">Ultimo Incontro</th>
                                <th className="p-4">Status Pagamenti</th>
                                <th className="p-4">Stato Percorso</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredClients.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center text-gray-400 italic">
                                        Nessun cliente trovato in questa sezione.
                                    </td>
                                </tr>
                            ) : filteredClients.map(client => {
                                const clientStats = stats[client.id] || { count: 0, lastDate: null, eta: '-' };
                                const lastDateClean = clientStats.lastDate
                                    ? new Date(clientStats.lastDate).toLocaleDateString('it-IT')
                                    : '-';

                                const paymentStatus = client.paymentStatus || 'Regolare';
                                const paymentColor =
                                    paymentStatus === 'Regolare' ? 'bg-green-100 text-green-700' :
                                        paymentStatus === 'In Sospeso' ? 'bg-amber-100 text-amber-700' :
                                            'bg-red-100 text-red-700';

                                return (
                                    <tr 
                                        key={client.id} 
                                        className="hover:bg-blue-50 transition cursor-pointer"
                                        onClick={() => onOpenClient && onOpenClient(client)}
                                    >
                                        <td className="p-4">
                                            <div className="font-bold text-gray-800">{client.name}</div>
                                            <div className="text-xs text-gray-400">{client.email}</div>
                                        </td>
                                        <td className="p-4 text-center">
                                            <div className="font-mono font-medium text-gray-700">{clientStats.eta} anni</div>
                                            {client.birthDate && (
                                                <div className="text-[10px] text-gray-400 mt-1">
                                                    🎂 {new Date(client.birthDate).toLocaleDateString('it-IT')}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 text-center font-mono font-medium text-gray-600">
                                            {clientStats.count}
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">
                                            {lastDateClean}
                                        </td>
                                        <td className="p-4" onClick={e => e.stopPropagation()}>
                                            <button
                                                onClick={() => handlePaymentToggle(client)}
                                                className={`px-3 py-1 rounded-full text-xs font-bold cursor-pointer hover:shadow-sm transition ${paymentColor}`}
                                            >
                                                {paymentStatus}
                                            </button>
                                        </td>
                                        <td className="p-4" onClick={e => e.stopPropagation()}>
                                            <select
                                                value={client.isActive === false ? 'concluso' : 'active'}
                                                onChange={(e) => handleStatusChange(client, e.target.value)}
                                                className={`p-2 rounded-lg text-sm font-bold border-2 cursor-pointer focus:outline-none ${client.isActive !== false
                                                    ? 'border-green-200 text-green-800 bg-green-50'
                                                    : 'border-gray-200 text-gray-600 bg-gray-50'
                                                    }`}
                                            >
                                                <option value="active">🟢 In Corso</option>
                                                <option value="concluso">🏁 Concluso</option>
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ReportsView;
